import React from 'react'
import { connect } from 'react-redux'
import styled from 'react-emotion'
import PinIcon from 'components/PinIcon'
import themeVars from 'ui_theme/site/globals/site.variables'
import { Flex, Label, IconButton } from 'shared/ui'
import { Checkbox } from 'util/ui'
import { pluralize } from 'util/strings'
import SpacedItems from 'shared/components/ui/SpacedItems'
import BulkStateBadge from 'components/StateBadge/Bulk'
import BulkAssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge/Bulk'
import ChangeInboxDropdown from 'components/App/DesktopView/shared/BulkChangeInboxDropdown'
import BulkStarIcon from 'components/Bulk/StarIcon'
import BulkSnoozeIcon from 'components/App/DesktopView/Bulk/DetailPane/SnoozeIcon'
import BulkTagIconV1 from 'components/App/DesktopView/Labels/LabelingDropdown/Bulk'
import BulkTagIconV2 from 'components/App/DesktopView/Labels/LabelingDropdownV2/Bulk'
import { selectSelectedTicketIds } from 'selectors/ticket_list/base'

import { translate } from './util'
import Count from './Count'
import SortMenu from './SortMenu'

export default function WideSwitcher(props) {
  const { isSelectionMode } = props

  if (isSelectionMode) return <BulkActionBar {...props} />

  return <WideBar {...props} />
}

// eslint-disable-next-line react/no-multi-comp
function WideBar(props) {
  const {
    canSeePinnedSearches,
    isFreeFormSearch,
    isSearchPinnable,
    onPinUnpinClick,
    onSelectAllClick,
    pinnedSearch,
    searchIsFolderSearch,
    ticketCount,
    title,
  } = props

  const getTitleLabel = () => {
    if (isFreeFormSearch) return 'Search results'
    if (searchIsFolderSearch) return title
    return translate(title)
  }

  const titleComponent = (
    <Flex
      noOverflow
      alignItems="baseline"
      css={`
        flex-grow: 1;
      `}
    >
      <div
        css={`
          font-size: 16px;
          line-height: 1.5;
          color: ${themeVars.dark};
          font-weight: ${themeVars.pageFontWeightSemiBold};
        `}
      >
        {getTitleLabel()}
      </div>
      <Count count={ticketCount} />
    </Flex>
  )

  return (
    <Flex
      alignItems="center"
      css={`
        min-height: 42px;
        padding-top: ${themeVars.mini};
        padding-bottom: ${themeVars.mini};
        padding-left: ${themeVars.big};
        padding-right: ${themeVars.large};
        border-bottom: 1px solid ${themeVars.dust};
      `}
    >
      <Checkbox
        checked={false}
        css={`
          margin-right: 16px;
        `}
        onClick={onSelectAllClick}
      />
      {titleComponent}
      <Flex>
        {canSeePinnedSearches &&
          isSearchPinnable && (
            <PinIcon
              size="small"
              isPinned={!!pinnedSearch}
              onPinClick={onPinUnpinClick}
            />
          )}
        <SortMenu ml={3} displayAsLabel />
      </Flex>
    </Flex>
  )
}

const BulkActionBarSpacedItemsContainer = styled(SpacedItems.Container)`
  width: 100%;
`

const BulkActionBar = connect(state => {
  return {
    selectedCount: selectSelectedTicketIds(state).length,
    ticketIds: selectSelectedTicketIds(state),
  }
})(props => {
  const {
    onUnselectAllClick,
    onMarkAsUnreadClick,
    onMarkAsSpamClick,
    onDeleteClick,
    ticketIds,
    selectedCount,
    isExperimentalLabelUiEnabled,
  } = props

  const BulkTagIcon = isExperimentalLabelUiEnabled
    ? BulkTagIconV2
    : BulkTagIconV1

  return (
    <Flex
      alignItems="center"
      css={`
        min-height: 42px;
        padding-top: ${themeVars.nano};
        padding-bottom: ${themeVars.nano};
        padding-left: ${themeVars.big};
        padding-right: ${themeVars.large};
        border-bottom: 1px solid ${themeVars.dust};
      `}
    >
      <Checkbox
        checked
        css={`
          margin-right: 16px;
        `}
        onClick={onUnselectAllClick}
      />

      <BulkActionBarSpacedItemsContainer
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="center"
        gap="mini"
      >
        <SpacedItems.Item>
          <BulkStateBadge variant="withIcon" />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <BulkAssignmentBadge iconSize={22} />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <BulkTagIcon key={`bulk-tag-${isExperimentalLabelUiEnabled}`} />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <BulkSnoozeIcon ticketIds={ticketIds} iconSize={16} />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <BulkStarIcon iconSize={22} />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <IconButton
            name="mail"
            size="small"
            iconSize={20}
            onClick={onMarkAsUnreadClick}
            tooltip="Mark as Unread"
          />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <IconButton
            name="error-outline"
            size="small"
            iconSize={19}
            onClick={onMarkAsSpamClick}
            tooltip="Mark as Spam"
            color="lava"
          />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <ChangeInboxDropdown
            ticketIds={ticketIds}
            trigger={
              <IconButton
                name="moveToInbox"
                svg
                size="small"
                iconSize={20}
                tooltip="Change Inbox..."
                color="sky"
              />
            }
          />
        </SpacedItems.Item>
        <SpacedItems.Item>
          <IconButton
            name="delete"
            size="small"
            iconSize={20}
            onClick={onDeleteClick}
            tooltip="Delete"
            color="lava"
          />
        </SpacedItems.Item>
        <SpacedItems.Item gap="auto">
          <Label size="small">
            {selectedCount} {pluralize(selectedCount, 'conversation')} selected
          </Label>
        </SpacedItems.Item>
      </BulkActionBarSpacedItemsContainer>
    </Flex>
  )
})
